import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import {
    HeartPulse,
    Scale,
    Sparkles,
    Activity,
    LineChart,
} from "lucide-react";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import PublishedMethodology from "./PublishedMethodology";

const LATEX = {
    multiRoute: String.raw`\begin{gathered}
\text{Steady-State Concentration}\;\text{(Multi-Route):} \\[0.3em]
C_{total}(t) = \sum_{i=1}^{n} R_i(t - t_i) \cdot \frac{1}{1 - e^{-k_i\tau_i}} \\[1.5em]
\text{Route-Specific Release Functions:} \\[0.3em]
R(t) = (A \cdot E \cdot k \cdot \alpha_{route}) \cdot e^{-kt} \quad \text{(depot, oral, peptide)} \\[0.3em]
\alpha_{route} \in \{\alpha_{depot}, \; \alpha_{aqueous}, \; \alpha_{peptide}, \; \alpha_{oral}\} \\[0.8em]
R(t) = \frac{D_{abs} \cdot k_a \cdot k_e}{k_a - k_e} \cdot \left(e^{-k_e t} - e^{-k_a t}\right) \quad \text{(transdermal)} \\[0.3em]
D_{abs} = \frac{V_{max} \cdot D_{applied}}{K_m + D_{applied}} \quad \text{(Michaelis-Menten absorption)} \\[1.5em]
\text{Exponential Decay:} \\[0.3em]
C(t) = C_0 \cdot e^{-kt}, \quad k = \frac{\ln(2)}{t_{1/2}} \\[1em]
{\scriptstyle \text{Parameters:}} \\[0.2em]
{\scriptstyle n = \text{total doses}, \quad t_i = \text{administration time}, \quad \tau = \text{dosing interval}} \\[0.3em]
{\scriptstyle A = \text{amount}, \quad E = \text{effectiveness}, \quad k = \text{decay constant}} \\[0.3em]
{\scriptstyle \alpha_{route} = \text{absorption rate modifier (route-dependent)}} \\[0.2em]
{\scriptstyle k_a = \text{transdermal absorption rate constant (site-specific)}} \\[0.2em]
{\scriptstyle k_e = \text{elimination rate constant}} \\[0.2em]
{\scriptstyle D_{abs} = \text{absorbed dose per administration}} \\[0.2em]
{\scriptstyle V_{max},\, K_m = \text{Michaelis-Menten saturation parameters}}
\end{gathered}`,
    aeRatio: String.raw`\begin{gathered}
\text{Total Androgenic Load (A):} \\[0.3em]
A = \max\!\left(1,\; T + \beta \cdot (DHT - DHT_{ref}) \cdot \phi\right) \\[0.8em]
\phi = \begin{cases}
1 & \text{if } DHT < DHT_{ref} \\[0.3em]
\left(\dfrac{DHT}{DHT_{ref}}\right)^{k-1} & \text{if } DHT \geq DHT_{ref}
\end{cases} \\[1.5em]
\text{A:E Ratio:} \\[0.3em]
\text{A:E} = \dfrac{A}{E_2} \\[1.5em]
{\scriptstyle \text{Parameters:}} \\[0.2em]
{\scriptstyle T = \text{Total Testosterone (ng/dL)}} \\[0.2em]
{\scriptstyle E_2 = \text{Estradiol (pg/mL)}} \\[0.2em]
{\scriptstyle DHT = \text{Dihydrotestosterone (ng/dL)}} \\[0.2em]
{\scriptstyle DHT_{ref} = \text{calibrated AR reference midpoint}} \\[0.2em]
{\scriptstyle \beta = \text{combined AR potency} = \alpha_{aff} \times \alpha_{res}} \\[0.2em]
{\scriptstyle \alpha_{aff} = \text{AR binding affinity relative to T}} \\[0.2em]
{\scriptstyle \alpha_{res} = \text{AR receptor residence time relative to T}} \\[0.2em]
{\scriptstyle k = \text{paracrine amplification exponent (above } DHT_{ref}\text{)}}
\end{gathered}`,
    cascade: String.raw`\begin{gathered}
\text{Cardiovascular Risk Gate:} \\[0.3em]
\Omega = \prod_{m \in \mathcal{M}} \mathbf{1}\!\left[\chi_m \notin \{\varnothing,\,\mathrm{amber}^{\uparrow},\,\mathrm{red}^{\uparrow}\}\right] \\[0.5em]
\mathcal{M} = \{\text{SBP},\; \text{DBP},\; \text{ApoB}|\text{LDL},\; \text{CRP},\; \text{BF\%},\; \text{HbA1c}\} \\[0.5em]
r = \sum_{m \in \mathcal{M}} \mathbf{1}[\chi_m = \mathrm{red}^{\uparrow}], \quad a = \sum_{m \in \mathcal{M}} \mathbf{1}[\chi_m = \mathrm{amber}^{\uparrow}] \\[1.5em]
\text{Graduated Hematocrit Ceiling:} \\[0.3em]
\left(H_{\text{green}}^{*},\, H_{\text{amber}}^{*}\right) = H^{\,\tau(r,\,a)} \\[0.5em]
\tau = \tau_{\max} \iff \Omega = 1 \\[1.5em]
\text{Dynamic Testosterone Range:} \\[0.3em]
g_m,\, a_m^{\uparrow},\, r_m^{\uparrow} = \mathbf{1}\!\left[\chi_m = \text{green},\, \mathrm{amber}^{\uparrow},\, \mathrm{red}^{\uparrow}\right] \\[0.5em]
\Delta U = \sum_{m \in \mathcal{M}_T} \!\left(\gamma_g\, g_m - \gamma_a\, a_m^{\uparrow} - \gamma_r\, r_m^{\uparrow}\right) \\[0.5em]
\Delta L = -\sum_{m \in \mathcal{M}_T} \!\left(\lambda_a\, a_m^{\uparrow} + \lambda_r\, r_m^{\uparrow}\right) \\[0.5em]
G_T^{*} = \min\!\left(\max(G_T^{\text{std}} + \Delta U,\, G_{\min}),\, G_{\max}\right) \\[0.5em]
A_T^{*} = G_T^{*} + w \\[0.5em]
L_T^{*} = \max\!\left(\min(L_T^{\text{std}} + \Delta L,\; L_T^{\text{std}}),\; L_{\min}\right) \\[0.5em]
\mathcal{M}_T = \{\text{SBP},\; \text{ApoB}|\text{LDL},\; \text{BF\%},\; \text{HbA1c},\; \underbrace{\text{CRP}}_{\text{conditional}}\} \\[1.5em]
\text{Hematocrit Cap:} \\[0.3em]
\chi_T = \begin{cases} \mathrm{amber} & \chi_T^{(0)} = \text{green}\; \land\; T \geq T_{\text{cap}} \\ & \quad \land\; \chi_{\text{Hct}} = \mathrm{red}^{\uparrow} \\ \chi_T^{(0)} & \text{otherwise} \end{cases} \\[1.5em]
\text{Full Cascade:} \\[0.3em]
\mathcal{M} \;\xrightarrow{\;\Omega,\, r,\, a\;}\; H^{\,\tau} \;\xrightarrow{\;f(\cdot)\;}\; \chi_{\text{Hct}} \\[0.5em]
\mathcal{M}_T \;\xrightarrow{\;\Delta U,\, \Delta L\;}\; \left(L_T^{*},\, G_T^{*}\right) \;\xrightarrow{\;f(\cdot)\;}\; \chi_T^{(0)} \;\xrightarrow{\;\chi_{\text{Hct}}\;}\; \chi_T \\[1.5em]
{\scriptstyle \Omega = \text{elite gate} \;(\mathbf{1} \iff \text{all six entered, none high})} \\[0.2em]
{\scriptstyle \mathcal{M} = \text{six cardiovascular markers}} \\[0.2em]
{\scriptstyle r,\, a = \text{count of red / amber high-side markers}} \\[0.2em]
{\scriptstyle \tau = \text{graduated hematocrit tier}} \\[0.2em]
{\scriptstyle \mathcal{M}_T = \text{five markers that move the T range}} \\[0.2em]
{\scriptstyle \uparrow = \text{high-side colour only}} \\[0.2em]
{\scriptstyle \gamma,\, \lambda = \text{per-marker upper / lower adjustments}} \\[0.2em]
{\scriptstyle G,\, L,\, w = \text{green ceiling, lower bound, amber width}} \\[0.2em]
{\scriptstyle T_{\text{cap}} = \text{hematocrit cap threshold}} \\[0.2em]
{\scriptstyle \chi_T^{(0)} = \text{T colour before the cap}} \\[0.2em]
{\scriptstyle \varnothing = \text{not entered}}
\end{gathered}`,
    cream: String.raw`\begin{gathered}
\text{Michaelis-Menten absorption:} \\[0.8em]
A = \dfrac{V_{\max} \cdot D}{K_m + D} \\[1.8em]
\text{Ester effectiveness adjustment:} \\[0.8em]
E = \dfrac{A}{\bar{\varepsilon}_{\,\text{cyp/enth}}} = \dfrac{1}{\bar{\varepsilon}_{\,\text{cyp/enth}}} \cdot \dfrac{V_{\max} \cdot D}{K_m + D} \\[1.8em]
r_{\text{eff}}(D) = \dfrac{V_{\max}}{K_m + D} \longrightarrow 0 \quad \text{as} \quad D \to \infty \\[1.8em]
\lim_{D \to 0}\, r_{\text{eff}} = \dfrac{V_{\max}}{K_m} \quad,\qquad E\big|_{D=K_m} = \dfrac{V_{\max}}{2\,\bar{\varepsilon}_{\,\text{cyp/enth}}} \\[1.8em]
{\scriptstyle A = \text{mg testosterone base absorbed}} \\[0.2em]
{\scriptstyle D = \text{weekly applied dose (mg/W)}} \\[0.2em]
{\scriptstyle E = \text{injectable ester equivalent (mg/W)}} \\[0.2em]
{\scriptstyle \bar{\varepsilon}_{\,\text{cyp/enth}} = \text{mean ester effectiveness}} \\[0.2em]
{\scriptstyle V_{\max} = \text{site-specific absorption ceiling}} \\[0.2em]
{\scriptstyle K_m = \text{site-specific half-saturation dose}} \\[0.2em]
{\scriptstyle r_{\text{eff}} = \text{effective absorption rate}}
\end{gathered}`,
    zakharov: String.raw`\begin{gathered}
\text{Complete System Equation:} \\[0.3em]
f([T]_{free}) = \\[0.5em]
[T]_{total} - [T]_{free} - K_{alb}[Alb][T]_{free} - [SHBG]\dfrac{\alpha + 2\alpha\beta}{1 + 2\alpha + \alpha\beta} \\[1.2em]
\text{SHBG Allosteric Binding:} \\[0.3em]
\theta_{SHBG} = \dfrac{\alpha + 2\alpha\beta}{1 + 2\alpha + \alpha\beta} \\[0.5em]
\alpha = K_{a1}[T]_{free}, \quad \beta = K_{a2}[T]_{free} \\[1.2em]
\text{Newton-Raphson Solution:} \\[0.3em]
[T]_{free}^{(n+1)} = [T]_{free}^{(n)} - \dfrac{f([T]_{free}^{(n)})}{f'([T]_{free}^{(n)})}
\end{gathered}`,
};

const MODELS = [
    {
        slug: "pk",
        icon: LineChart,
        label: "Multi-Route PK",
        title: "Multi-Route Compound Plotter",
        powers: "Powers PK charting across every compound",
        body: "A unified pharmacokinetic engine that handles injectable oils, orals, peptides, and testosterone cream — each modeled accurately for its specific delivery route, with route-specific absorption and elimination kinetics.",
        latex: LATEX.multiRoute,
        span: 3,
        tall: true,
    },
    {
        slug: "ae",
        icon: Scale,
        label: "Androgenic Load",
        title: "Androgen:Estrogen Asymmetric Additive Formula",
        powers: "Powers your A:E ratio analysis",
        body: "Incorporates DHT's ~10× androgen receptor potency and paracrine amplification — beyond T:E alone. Handles suppressed and elevated DHT non-linearly, producing a truer picture of total androgenic load.",
        latex: LATEX.aeRatio,
        span: 3,
        tall: true,
    },
    {
        slug: "cascade",
        icon: HeartPulse,
        label: "Cardio-Metabolic Risk",
        title: "The Cardio-Metabolic Cascade Model",
        powers: "Powers your biomarker score & T range",
        body: "Six markers — Blood Pressure, ApoB, CRP, Body Fat, HbA1c — cascade into your hematocrit threshold, which sets your personalized testosterone reference range. Lean, metabolically healthy users earn a higher ceiling.",
        latex: LATEX.cascade,
        span: 2,
        dense: true,
    },
    {
        slug: "cream",
        icon: Sparkles,
        label: "Cream Absorption",
        title: "Michaelis-Menten Saturation Model",
        powers: "Powers transdermal testosterone modeling",
        body: "Cream absorption is non-linear — skin transport proteins and follicular shunts saturate at higher doses. Modeled with site-specific bioavailability for scrotum, abdomen, thighs, and shoulders.",
        latex: LATEX.cream,
        span: 2,
        tall: true,
        dense: true,
    },
    {
        slug: "freet",
        icon: Activity,
        label: "Free Testosterone",
        title: "Zakharov Nonlinear Allosteric Model",
        powers: "Powers your Free T calculation",
        body: "Reflects how SHBG dimers actually behave under elevated androgenic load. Significantly more accurate than Vermeulen — the population-level standard — at TRT and supraphysiological levels.",
        latex: LATEX.zakharov,
        span: 2,
        tall: true,
        dense: true,
    },
];

function FormulaPanel({ latex, dense = false, tall = false }) {
    const alignClass = dense || tall ? "items-start" : "items-center";
    const scrollRef = useRef(null);
    const innerRef = useRef(null);

    useEffect(() => {
        const scrollEl = scrollRef.current;
        const innerEl = innerRef.current;
        if (!scrollEl || !innerEl) return;

        const fit = () => {
            const katexEl = innerEl.querySelector(".katex");
            if (!katexEl) return;
            // Reset inline font first so we measure the CSS-governed natural width
            katexEl.style.fontSize = "";
            // The scroll container has 16px horizontal padding each side (px-4).
            // clientWidth includes its padding, so subtract it to get the real content viewport.
            const available = scrollEl.clientWidth - 32;
            if (available <= 0) return;
            const first = katexEl.scrollWidth;
            // Only scale if it overflows by more than 1px (ignore subpixel rounding)
            if (first <= available + 1) return;
            for (let i = 0; i < 4; i++) {
                const natural = katexEl.scrollWidth;
                if (natural <= available) break;
                const ratio = (available / natural) * 0.985; // slight headroom
                const baseSize = parseFloat(getComputedStyle(katexEl).fontSize);
                const next = Math.max(9, baseSize * ratio);
                katexEl.style.fontSize = `${next}px`;
                if (next <= 9) break;
            }
        };

        // Initial fit (wait a frame for KaTeX to render)
        const t = setTimeout(fit, 0);
        // ResizeObserver callbacks must not synchronously mutate observed
        // elements — doing so triggers the "ResizeObserver loop completed
        // with undelivered notifications" warning. Defer fit() to the next
        // animation frame and dedupe rapid resize notifications.
        let rafId = 0;
        const onResize = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                fit();
            });
        };
        const ro = new ResizeObserver(onResize);
        ro.observe(scrollEl);
        return () => {
            clearTimeout(t);
            if (rafId) cancelAnimationFrame(rafId);
            ro.disconnect();
        };
    }, [latex]);

    return (
        <div
            className="formula-panel relative rounded-xl border border-white/8 px-4 py-6 lg:py-7 my-5 overflow-hidden h-[260px]"
            style={{
                background:
                    "radial-gradient(circle at 50% 50%, rgba(43,166,224,0.10), transparent 70%), #06070a",
            }}
        >
            <div
                ref={scrollRef}
                className={`formula-scroll absolute inset-0 overflow-y-auto overflow-x-hidden px-4 py-6 lg:py-7 flex justify-center ${alignClass} ${dense ? "formula-dense" : ""}`}
            >
                <div
                    ref={innerRef}
                    className="formula-content text-white/90 text-center w-full"
                >
                    <BlockMath math={latex} />
                </div>
            </div>
        </div>
    );
}

function ModelCard({ model, idx }) {
    return (
        <motion.article
            id={`formula-${model.slug}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: idx * 0.05 }}
            className="tile p-6 lg:p-7 lg:col-span-[var(--span)] scroll-mt-32"
            style={{ "--span": model.span, gridColumn: `span ${model.span}` }}
            data-testid={`model-card-${model.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
            <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#2ba6e0]/10 border border-[#2ba6e0]/30 flex items-center justify-center shrink-0">
                    <model.icon size={18} className="text-[#49bef5]" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-[#49bef5] mb-1">
                        {model.label}
                    </div>
                    <h3 className="font-display font-semibold text-white text-[18px] lg:text-[19px] leading-tight">
                        {model.title}
                    </h3>
                </div>
            </div>

            <FormulaPanel latex={model.latex} dense={model.dense} tall={model.tall} />

            <div className="text-[11px] uppercase tracking-[0.16em] font-mono text-white/45 mb-2">
                {model.powers}
            </div>
            <p className="text-white/65 text-[14px] leading-relaxed">
                {model.body}
            </p>
        </motion.article>
    );
}

export default function UnderTheHood() {
    return (
        <section
            id="under-the-hood"
            data-testid="under-the-hood-section"
            className="relative py-24 lg:py-32 bg-black overflow-hidden border-t border-white/5"
        >
            <div
                className="absolute inset-0 grid-backdrop opacity-25 pointer-events-none"
                aria-hidden
            />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-end mb-12 lg:mb-16">
                    <div>
                        <div className="eyebrow mb-6">
                            <span className="dot" /> Under the Hood
                        </div>
                        <h2 className="font-display font-bold clamp-h2 text-white">
                            Real engineering.
                            <br />
                            <span className="blue-glow">Real math.</span>
                        </h2>
                    </div>
                    <p className="text-white/65 text-base lg:text-lg leading-relaxed lg:pb-2">
                        Every score, range, and insight in TRT Plus is produced
                        by a deterministic, programmatically engineered model —
                        not approximated by a generic AI. Clinical-grade
                        analytical depth.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
                    {MODELS.map((m, i) => (
                        <ModelCard key={m.title} model={m} idx={i} />
                    ))}
                </div>

                <p className="mt-10 text-[12px] font-mono text-white/40 max-w-2xl">
                    Notation simplified for display. Full derivations and
                    parameters documented in the in-app knowledge base.
                </p>

                {/* Published methodology — 4 peer-published technical
                    notes on Zenodo. Sits after the formula grid so the
                    formulas themselves are the headline; the papers are
                    the receipts. */}
                <div className="mt-16 lg:mt-20">
                    <PublishedMethodology useFullTitle={true} />
                </div>
            </div>
        </section>
    );
}
